"use client";

import React from "react";
import Link from "next/link";
import { 
  Zap, 
  CheckCircle2,
  Server,
  Code,
  Shield,
  Search
} from "lucide-react";
import { Footer } from "@/components/LandingLayout";

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100 font-sans antialiased transition-colors duration-300">
      {/* Hero Section */}
      <section className="relative pt-10 pb-12 lg:pt-16 lg:pb-20 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full pointer-events-none -z-10">
          <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-600/5 dark:bg-blue-600/10 blur-[120px] rounded-full"></div>
        </div>
        <div className="container mx-auto px-4 max-w-5xl relative z-10">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-zinc-100 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 text-zinc-600 dark:text-zinc-400 text-[10px] font-bold uppercase tracking-widest mb-6">
              <div className="w-1 h-1 rounded-full bg-green-500 animate-pulse"></div>
              Official TeraBox Extraction API
            </div>
            <h1 className="text-3xl lg:text-5xl font-black tracking-tight mb-4 text-zinc-900 dark:text-white leading-tight">
              Powerful Extraction Engine <br /> for Developers.
            </h1>
            <h2 className="text-sm lg:text-base text-zinc-700 dark:text-zinc-400 leading-relaxed max-w-xl mx-auto mb-8 font-medium">
              High-speed link extraction for files and folders. One API call gets you metadata, download links, and direct stream URLs.
            </h2>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link href="/sign-up-login-screen">
                <button className="w-full sm:w-auto px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded-lg transition-all shadow-lg shadow-blue-600/10 uppercase tracking-wider">
                  Start Building Now
                </button>
              </Link>
              <Link href="/docs">
                <button className="w-full sm:w-auto px-6 py-2.5 bg-zinc-100 dark:bg-zinc-900 text-zinc-900 dark:text-white font-bold text-xs rounded-lg border border-zinc-200 dark:border-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-800 transition-all uppercase tracking-wider">
                  Read API Docs
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Features Grid */}
      <section className="py-12 border-y border-zinc-200 dark:border-zinc-900/50 bg-zinc-50 dark:bg-zinc-900/5">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { 
                title: "100 Free Requests", 
                desc: "Every account gets a monthly free quota to test and scale.", 
                icon: <Zap className="w-4 h-4 text-blue-600 dark:text-blue-500" /> 
              },
              { 
                title: "Direct Stream URLs", 
                desc: "Get instant access to playable video streams in every response.", 
                icon: <Server className="w-4 h-4 text-emerald-600 dark:text-emerald-500" /> 
              },
              { 
                title: "Smart Extraction", 
                desc: "Automatically handles mirrors and redirects for any share link.", 
                icon: <Search className="w-4 h-4 text-purple-600 dark:text-purple-500" /> 
              },
            ].map((f, i) => (
              <div key={i} className="flex gap-4 p-5 bg-white dark:bg-zinc-900/20 border border-zinc-200 dark:border-zinc-800/50 rounded-xl shadow-sm dark:shadow-none">
                <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-zinc-100 dark:bg-zinc-900 flex items-center justify-center">
                  {f.icon}
                </div>
                <div>
                  <h3 className="text-xs font-bold mb-1 text-zinc-900 dark:text-white uppercase tracking-wide">{f.title}</h3>
                  <p className="text-[11px] text-zinc-600 dark:text-zinc-500 leading-relaxed font-medium">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Value Section */}
      <section className="py-24">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="flex flex-col lg:flex-row items-center gap-16">
            <div className="lg:w-1/2">
              <h2 className="text-2xl lg:text-3xl font-bold mb-6 text-zinc-900 dark:text-white leading-tight">Scale your application <br /> with industrial-grade API.</h2>
              <p className="text-sm text-zinc-700 dark:text-zinc-400 mb-8 leading-relaxed font-medium">
                Our infrastructure handles the complexity of TeraBox authentication, cookie rotation, and rate limiting so you can focus on building your app.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  "No Daily Limits",
                  "12 Req/Sec Speed",
                  "Folder Support",
                  "99.9% Uptime",
                  "Simple API Key",
                  "Standard JSON"
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-blue-600" />
                    <span className="text-xs text-zinc-800 dark:text-zinc-300 font-bold">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:w-1/2 w-full">
              <div className="bg-zinc-50 dark:bg-zinc-900 p-6 rounded-2xl border border-zinc-200 dark:border-zinc-800 shadow-xl dark:shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-blue-600/50"></div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-red-500/50"></div>
                    <div className="w-2 h-2 rounded-full bg-amber-500/50"></div>
                    <div className="w-2 h-2 rounded-full bg-emerald-500/50"></div>
                  </div>
                  <span className="text-[10px] font-bold text-zinc-500 dark:text-zinc-500 uppercase tracking-widest">v1/extract</span>
                </div>
                <pre className="font-mono text-[11px] leading-relaxed text-blue-700 dark:text-blue-400 overflow-x-auto">
                  <code>{`{
  "errno": 0,
  "request_id": "834729123",
  "list": [
    {
      "server_filename": "video_hd.mp4",
      "size": 1073741824,
      "dlink": "https://...",
      "stream_url": "https://..."
    }
  ]
}`}</code>
                </pre>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <div className="p-12 bg-blue-600 rounded-3xl relative overflow-hidden shadow-2xl">
            <h2 className="text-2xl lg:text-4xl font-black text-white mb-4 tracking-tight">Ready to start?</h2>
            <p className="text-blue-50 text-sm mb-8 opacity-90 font-medium">
              Get your API key instantly and start extracting. No credit card required.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link href="/sign-up-login-screen">
                <button className="px-8 py-3 bg-white text-blue-700 font-bold text-xs rounded-lg uppercase tracking-wider shadow-lg hover:scale-105 transition-transform">
                  Get Free API Key
                </button>
              </Link>
              <Link href="/pricing" className="px-8 py-3 bg-blue-800/40 text-white font-bold text-xs rounded-lg border border-white/10 uppercase tracking-wider hover:bg-blue-800/60 transition-colors">
                View Pricing
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
